/** A group's checkbox in a picker: every pickable member selected, some members selected, or none. */
export type GroupSelectionState = "checked" | "partial" | "unchecked";

/**
 * The tri-state of a group's checkbox. Only members in `availableIds` count towards `checked`, so a
 * group whose unavailable members (e.g. devices taken by another PPA) are left out still shows as
 * fully selected. A group with no available member is never `checked`.
 */
export const groupSelectionState = <T>(
  groupIds: readonly T[],
  availableIds: readonly T[],
  selectedIds: readonly T[]
): GroupSelectionState => {
  const selected = new Set(selectedIds);
  const available = new Set(availableIds);
  const pickable = groupIds.filter((id) => available.has(id));
  if (pickable.length > 0 && pickable.every((id) => selected.has(id))) {
    return "checked";
  }
  return groupIds.some((id) => selected.has(id)) ? "partial" : "unchecked";
};

/**
 * The selection after clicking a group's checkbox: a `checked` group deselects its available
 * members, any other adds the available members not yet selected. An unavailable member is never
 * added nor removed, and the existing selection keeps its order.
 */
export const toggleGroup = <T>(
  groupIds: readonly T[],
  availableIds: readonly T[],
  selectedIds: readonly T[]
): T[] => {
  const available = new Set(availableIds);
  const pickable = groupIds.filter((id) => available.has(id));
  if (groupSelectionState(groupIds, availableIds, selectedIds) === "checked") {
    const removed = new Set(pickable);
    return selectedIds.filter((id) => !removed.has(id));
  }
  const selected = new Set(selectedIds);
  return [...selectedIds, ...pickable.filter((id) => !selected.has(id))];
};
